import * as React from 'react';
import Box from '@mui/material/Box';
import { BRAND } from '../theme/landingTheme';

const SIZE = 56;
const STROKE = 3;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// Floating scroll widget: the Marter logo spins as you scroll while a yellow
// ring fills with page progress. Click scrolls back to top.
export default function ScrollFab() {
  const [visible, setVisible] = React.useState(false);
  const ringRef = React.useRef(null);
  const logoRef = React.useRef(null);
  const ticking = React.useRef(false);

  React.useEffect(() => {
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const progress = max > 0 ? Math.min(y / max, 1) : 0;
        if (ringRef.current) {
          ringRef.current.style.strokeDashoffset = String(CIRCUMFERENCE * (1 - progress));
        }
        if (logoRef.current && !reduced) {
          logoRef.current.style.transform = `rotate(${y * 0.35}deg)`;
        }
        setVisible(y > 300);
        ticking.current = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Box
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      role="button"
      aria-label="Scroll back to top"
      sx={{
        position: 'fixed',
        bottom: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 },
        width: SIZE,
        height: SIZE,
        zIndex: 1200,
        cursor: 'pointer',
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1)' : 'scale(0.6)',
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        borderRadius: '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(6px)',
        boxShadow: '0 4px 16px rgba(26, 26, 26, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        width={SIZE}
        height={SIZE}
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
        aria-hidden="true"
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="rgba(255, 202, 24, 0.2)"
          strokeWidth={STROKE}
        />
        <circle
          ref={ringRef}
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={BRAND.yellow}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE}
        />
      </svg>
      <Box
        component="img"
        ref={logoRef}
        src="/logo192.png"
        alt=""
        sx={{ width: 30, height: 30, willChange: 'transform' }}
      />
    </Box>
  );
}
